import {naturalSortByKey, objectFromEntries} from '@benchling/js-utils';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import TemplateChangelog from 'config-migration/import/templates-step/TemplateChangelog';
import TemplatePlan from 'config-migration/import/templates-step/TemplatePlan';
import ProcessTaskSchemaPlanSection from 'config-migration/import/workflows-step/ProcessTaskSchemaPlanSection';
import {MigrationObject, ObjectWithAttributes, TemplateAttributes} from 'config-migration/MigrationConstants';
import {OBJECT_TYPE_TO_DISPLAY_TEXT, ObjectType} from 'config-migration/types/ObjectType';
import {customSchemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import lazyInit from 'util/lazyInit';

import {MigrationStep, PlannableMigrationStep} from '../types/MigrationStep';
import {ImportChangelogSection, ImportChangelogSectionProps} from './import-changelog/ImportChangelog';
import ImportPlanSection, {ImportPlanSectionProps} from './import-plan-sections/ImportPlanSection';
import {isTemplatePlannedOnStep} from './planning-logic/templateFiltering';

/**
 * Whether `PrimaryObject`s should be mapped based on their name during planning.
 *
 * During planning (if no provenance id is available), we let the user decide what destination object to map
 *  their source object to. Source objects in this list should be mapped to destination objects with matching
 *  names by default.
 *
 * Note: `ChildObject`s automatically default to name-matching.
 */
export const DEFAULT_NAME_MATCHED_OBJECT_TYPES = ['TemplateCollection'];

/** The tabs that will be rendered in the ImportWizard's step UI. */
export const MIGRATION_PLAN_TABS = [
  {
    migrationStep: MigrationStep.SCHEMAS,
    title: 'Schemas',
    isEnabled: () => true,
  },
  {
    migrationStep: MigrationStep.TEMPLATES,
    title: 'Templates',
    isEnabled: () => true,
  },
  {
    migrationStep: MigrationStep.PROCESS_TASK_SCHEMAS,
    title: 'Workflows',
    isEnabled: () => BenchlingConfig.WORKFLOWS_ENABLE,
  },
  {
    migrationStep: MigrationStep.STUDIES,
    title: 'Studies',
    isEnabled: () => BenchlingConfig.STUDIES_ENABLE,
  },
];

const CHILDLESS_OBJECT_TYPES = [
  ObjectType.Subtemplate,
  ObjectType.Template,
  ObjectType.TemplateCollection,
  ObjectType.ProcessFlowchartConfigVersion,
  // TODO(BNCH-106538): Support child objects in UI for procedures
  ObjectType.Procedure,
  ObjectType.TestDefinition,
] as const;
export type ObjectTypeWithChild = Exclude<ObjectType, (typeof CHILDLESS_OBJECT_TYPES)[number]>;
export const OBJECT_TYPE_TO_CHILD_DISPLAY_TEXT: {[objectType in ObjectTypeWithChild]: string} = {
  EntitySchema: 'Schema fields',
  Dropdown: 'Dropdown options',
  RunSchema: 'Schema fields',
  ResultSchema: 'Schema fields',
  BatchSchema: 'Schema fields',
  BoxSchema: 'Schema fields',
  ContainerSchema: 'Schema fields',
  LocationSchema: 'Schema fields',
  PlateSchema: 'Schema fields',
  ProcessTaskSchema: 'Schema fields',
  ProcessOutputSchema: 'Output schema fields',
  StudySchema: 'Schema fields',
  Fieldset: `${customSchemaFieldsetName()} fields`,
};

export const ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP: Record<PlannableMigrationStep, Array<ObjectType>> = {
  [MigrationStep.SCHEMAS]: [
    ObjectType.Dropdown,
    ObjectType.EntitySchema,
    ObjectType.BatchSchema,
    ObjectType.LocationSchema,
    ObjectType.BoxSchema,
    ObjectType.PlateSchema,
    ObjectType.ContainerSchema,
    ObjectType.RunSchema,
    ObjectType.ResultSchema,
    ObjectType.Fieldset,
    ObjectType.TestDefinition,
  ],
  [MigrationStep.TEMPLATES]: [
    ObjectType.TemplateCollection,
    // Note: We need to exclude Execution Templates i.e. Templates w/`processTaskSchemaId` in their
    //  objectAttributes
    ObjectType.Template,
    ObjectType.Subtemplate,
    ObjectType.Procedure,
  ],
  [MigrationStep.PROCESS_TASK_SCHEMAS]: [
    ObjectType.ProcessTaskSchema,
    ObjectType.ProcessOutputSchema,
    ObjectType.ProcessFlowchartConfigVersion,
    // Note: We only want to include Execution Templates i.e. Templates w/`processTaskSchemaId` in their
    //  objectAttributes
    ObjectType.Template,
  ],
  [MigrationStep.STUDIES]: [ObjectType.StudySchema],
};

/**
 * Object types that remove children instead of archiving them. The mapping values are the children that
 * are removed, in the case where the parent object has multiple child types.
 *
 * TODO(BNCH-100932): Return whether the child is removed or archived instead of general "Subtraction" action
 */
export const OBJECT_TYPE_TO_REMOVED_CHILD_CLASS: Map<ObjectType, string> = new Map([
  [ObjectType.Fieldset, 'SchemaField'],
]);

/**
 * TODO(BNCH-65943): We only use keep logic on things outside of this list - because the change log is not
 *  reliable for templates and subtemplates due to gaps with the BE's diffing logic. Templates currently don't
 *  have keep logic, they only have Unmatched.
 */
export const KEPT_OBJECT_TYPE_IGNORELIST = [
  ObjectType.Subtemplate,
  ObjectType.Template,
  ObjectType.TemplateCollection,
];

/**
 * Object types that can be grouped by template collection.
 */
export const TEMPLATE_COLLECTIBLE_OBJECT_TYPES: Array<ObjectType> = [
  ObjectType.Template,
  ObjectType.Subtemplate,
  ObjectType.Procedure,
];

/**
 * An interface for the information needed to display the configuration options for a section
 * of objects with the same Object Type(s).
 *
 * This functions as a more abstract representation of the various sections of object types supported.
 *
 * E.g. used to
 *  - generate object type groups in ImportErrorLogs
 *  - combine with a set of MigrationObjectPlans to generate a MigrationPlanSection
 */
export interface ObjectSectionConfig {
  /** The object types to display in this section.  */
  objectTypes: Array<ObjectType>;
  /** How to generate the name of each section in the config pane. */
  displayNameFn: <T extends MigrationObject>(objects: Array<T>) => string;
  /**
   * The component to use when displaying this section in the changelog. Unless you need custom logic,
   * use `ImportChangelogSection`.
   */
  changelogComponent: (props: ImportChangelogSectionProps) => JSX.Element;
  /** The component to use when displaying this section for planning in the ImportWizard. Unless you need
   * custom logic, use `ImportPlanSection`. */
  planComponent: (props: ImportPlanSectionProps) => JSX.Element;
  /** The function to use when grouping the objects within this section. This is generally done by sorting on
   * name or on the object types supported within the section. Generally sorting the objects on
   * `objectDisplayName` is sufficient.
   */
  groupRelevantObjects: <T extends MigrationObject>(objects: Array<T>) => Array<Array<T>>;
  /** What tab this section should be displayed on. */
  migrationTab: MigrationStep;
  /** An optional function to return if an object that would be this section should be excluded anyway. */
  shouldExcludeObjectWithAttributes?: <T extends ObjectWithAttributes>(object: T) => boolean;
}

/* Create a separate section for one type of Schema from the SCHEMAS step */
const mapSchemaToObjectSectionConfig = (objectType: ObjectType): ObjectSectionConfig => ({
  objectTypes: [objectType],
  displayNameFn: <T extends MigrationObject>(_: Array<T>): string =>
    pluralize(2, OBJECT_TYPE_TO_DISPLAY_TEXT[objectType]),
  changelogComponent: ImportChangelogSection,
  planComponent: ImportPlanSection,
  groupRelevantObjects: <T extends MigrationObject>(objects: Array<T>) => [
    naturalSortByKey(objects, 'objectDisplayName'),
  ],
  migrationTab: MigrationStep.SCHEMAS,
});

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section for all objects used in the TEMPLATES step. */
export const getSortedObjectSectionsForTemplates = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.TEMPLATES],
  displayNameFn: <T extends MigrationObject>(objects: Array<T>): string => {
    const templateCollectionObject = objects.find(
      (object) => object.objectType === ObjectType.TemplateCollection
    )!;
    return templateCollectionObject.objectDisplayName;
  },
  changelogComponent: TemplateChangelog,
  planComponent: TemplatePlan,
  // Do not show Execution Templates within the Template pane - they are shown on the Workflows Pane
  shouldExcludeObjectWithAttributes: <T extends ObjectWithAttributes>(object: T) =>
    object.objectType === ObjectType.Template && !isTemplatePlannedOnStep(object, MigrationStep.TEMPLATES),
  groupRelevantObjects: <T extends MigrationObject>(objects: Array<T>) => {
    const templatesByCollectionId = _.groupBy(
      objects.filter((object) => TEMPLATE_COLLECTIBLE_OBJECT_TYPES.includes(object.objectType)),
      (object) => (object.objectAttributes as TemplateAttributes).templateCollectionId
    );

    return naturalSortByKey(
      objects.filter((object) => object.objectType === ObjectType.TemplateCollection),
      'objectDisplayName'
    ).map((collection) => [
      collection,
      ...naturalSortByKey(templatesByCollectionId[collection.objectId] || [], 'objectDisplayName'),
    ]);
  },
  migrationTab: MigrationStep.TEMPLATES,
}));

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section for all objects used in the PROCESS_TASK_SCHEMAS (Workflows) step. */
export const getSortedObjectSectionsForProcessTaskSchemas = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.PROCESS_TASK_SCHEMAS],
  displayNameFn: <T extends MigrationObject>(_: Array<T>): string =>
    pluralize(2, OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.ProcessTaskSchema]),
  changelogComponent: ImportChangelogSection,
  planComponent: ProcessTaskSchemaPlanSection,
  // Do not show non-Execution Templates within the Workflows pane - they are shown on the Templates Pane
  shouldExcludeObjectWithAttributes: <T extends ObjectWithAttributes>(object: T) =>
    object.objectType === ObjectType.Template &&
    !isTemplatePlannedOnStep(object, MigrationStep.PROCESS_TASK_SCHEMAS),
  groupRelevantObjects: <T extends MigrationObject>(objects: Array<T>) => [
    naturalSortByKey(objects, 'objectDisplayName'),
  ],
  migrationTab: MigrationStep.PROCESS_TASK_SCHEMAS,
}));

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section for all objects used in the StudySchema (Studies) step. */
export const getSortedObjectSectionsForStudies = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.STUDIES],
  displayNameFn: <T extends MigrationObject>(_: Array<T>): string =>
    pluralize(2, OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.StudySchema]),
  changelogComponent: ImportChangelogSection,
  planComponent: ImportPlanSection,
  groupRelevantObjects: <T extends MigrationObject>(objects: Array<T>) => [
    naturalSortByKey(objects, 'objectDisplayName'),
  ],
  migrationTab: MigrationStep.STUDIES,
}));

// We lazy-init this array due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
export const getSortedObjectSections = lazyInit<Array<ObjectSectionConfig>>(() => [
  ...ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.SCHEMAS].map((objectType) =>
    mapSchemaToObjectSectionConfig(objectType)
  ),
  getSortedObjectSectionsForTemplates(),
  getSortedObjectSectionsForProcessTaskSchemas(),
  getSortedObjectSectionsForStudies(),
]);

/**
 * We lazy-init this as it references the also lazily initialized getSortedObjectSections
 *
 * WARNING: This maps each objectType to MigrationStep, but certain types (e.g. Template) can belong to
 * multiple MigrationSteps (see shouldExcludeObjectWithAttributes). This function does not account for that.
 * Prefer using migrationObjectToTab instead.
 */
export const getObjectTypeToTab = lazyInit<{[objectType: string]: MigrationStep}>(() =>
  objectFromEntries(
    getSortedObjectSections().flatMap((objectSectionConfig) =>
      objectSectionConfig.objectTypes.map((objectType: string): [string, MigrationStep] => [
        objectType,
        objectSectionConfig.migrationTab,
      ])
    )
  )
);

export function migrationObjectToTab<T extends ObjectWithAttributes>(object: T): MigrationStep {
  const filteredSections = getSortedObjectSections().filter(
    (objectSectionConfig) =>
      objectSectionConfig.objectTypes.includes(object.objectType) &&
      !(objectSectionConfig.shouldExcludeObjectWithAttributes?.(object) ?? false)
  );
  if (filteredSections.length !== 1) {
    assertAndContinue(
      false,
      `Expected to find exactly one section for ${object}, but found ${filteredSections.length}: ${filteredSections}`
    );
    return getObjectTypeToTab()[object.objectType];
  }
  return filteredSections[0].migrationTab;
}

/**
 * Given a collection of MigrationObjects, maps them into an array of objects and their relevant
 * ObjectSectionConfigs.
 */
export function generateSections<T extends MigrationObject>(
  objects: Array<T>
): Array<{objects: Array<T>; config: ObjectSectionConfig}> {
  const objectsByType = _.groupBy(objects, 'objectType');

  return getSortedObjectSections().flatMap((sectionConfig) => {
    const relevantObjects = sectionConfig.objectTypes
      .flatMap((objectType) => objectsByType[objectType] || [])
      .filter((object) => !(sectionConfig.shouldExcludeObjectWithAttributes?.(object) ?? false));

    if (!relevantObjects.length) {
      return [];
    }
    const objectGroups = sectionConfig.groupRelevantObjects(relevantObjects);
    return objectGroups.map((group) => ({
      objects: group,
      config: sectionConfig,
    }));
  });
}

/** The list of Migration Steps where we do planning (and can filter objects) */
export function getPlanableMigrationSteps(): Array<MigrationStep> {
  return MIGRATION_PLAN_TABS.map((tabConfig) => tabConfig.migrationStep);
}

export function getCurrentAndLaterPlanSteps(currentStep: MigrationStep): Array<MigrationStep> {
  const orderedSteps = getPlanableMigrationSteps();
  const stepIndex = orderedSteps.findIndex((step) => step === currentStep);
  return orderedSteps.slice(stepIndex);
}

function filterMigrationObjects<T extends ObjectWithAttributes>(
  migrationObjects: Array<T>,
  currentStep: MigrationStep,
  includePriorSteps: boolean
): Array<T> {
  const orderedSteps = getPlanableMigrationSteps();
  assertAndContinue(
    orderedSteps.includes(currentStep),
    `Only allow filtering to SCHEMAS or TEMPLATES step, but was ${currentStep}`
  );
  const currentStepIndex = orderedSteps.findIndex((step) => step === currentStep);
  const validIndices = includePriorSteps
    ? Array.from(Array(currentStepIndex + 1).keys())
    : [currentStepIndex];

  const objectsByType = _.groupBy(migrationObjects, 'objectType');
  return getSortedObjectSections().flatMap((objectSectionConfig) => {
    const sectionStepIndex = orderedSteps.findIndex((step) => step === objectSectionConfig.migrationTab);
    if (!validIndices.includes(sectionStepIndex)) {
      return [];
    }
    const objectsOfRelevantType = objectSectionConfig.objectTypes.flatMap(
      (objectType) => objectsByType[objectType] || []
    );
    return objectsOfRelevantType.filter(
      (object) => !(objectSectionConfig.shouldExcludeObjectWithAttributes?.(object) ?? false)
    );
  });
}

export function filterMigrationObjectsToAllPlannedSteps<T extends ObjectWithAttributes>(
  migrationObjects: Array<T>,
  currentStep: MigrationStep
): Array<T> {
  return filterMigrationObjects(migrationObjects, currentStep, true);
}

export function filterMigrationObjectsToCurrentStep<T extends ObjectWithAttributes>(
  migrationObjects: Array<T>,
  currentStep: MigrationStep
): Array<T> {
  return filterMigrationObjects(migrationObjects, currentStep, false);
}
